import * as amplitude from "@amplitude/analytics-browser";
import { Box, Button, Flex, Icon, Tag, Text, VStack } from "@chakra-ui/react";
import { FieldMetaProps, Formik } from "formik";
import { noop } from "lodash";
import { useCallback } from "react";
import { RiCheckLine, RiFlaskLine } from "react-icons/ri";
import { FormMustacheInput } from "src/components/Inputs/FormMustacheInput";
import { ResizableDrawer } from "src/components/Layout/ResizableDrawer";
import FormikLexicalEditor from "src/components/WysiwygEditor/lexical/FormikEditor";
import type { MessageContextSchema } from "src/generated/hooks/avela-ai/requests";
import { MessageContent } from "../schemas";
import {
  emailEditorSettings,
  smsEditorSettings,
} from "./lexicalEditorSettings";

type PolishPreviewDrawerProps = {
  isOpen: boolean;
  polishedMessage: MessageContent;
  hasRecipientsWithEmail: boolean;
  hasRecipientsWithPhone: boolean;
  onConfirm: (polishedMessage: MessageContent) => void;
  onCancel: () => void;
  messageContext?: MessageContextSchema;
};

export function PolishPreviewDrawer({
  isOpen,
  polishedMessage,
  hasRecipientsWithEmail,
  hasRecipientsWithPhone,
  onConfirm,
  onCancel,
  messageContext,
}: PolishPreviewDrawerProps) {
  const handleConfirm = useCallback(
    (
      values: MessageContent,
      getFieldMeta: (name: string) => FieldMetaProps<MessageContent>
    ) => {
      signalPolishEdited(getFieldMeta, messageContext);
      onConfirm(values);
    },
    [onConfirm, messageContext]
  );

  const handleCancel = useCallback(
    (
      values: MessageContent,
      getFieldMeta: (name: string) => FieldMetaProps<MessageContent>
    ) => {
      signalPolishEdited(getFieldMeta, messageContext);
      onCancel();
    },
    [onCancel, messageContext]
  );

  return (
    <Formik<MessageContent>
      initialValues={polishedMessage}
      enableReinitialize
      onSubmit={noop}
    >
      {({ values, getFieldMeta }) => (
        <ResizableDrawer
          isOpen={isOpen}
          onClose={() => handleCancel(values, getFieldMeta)}
          closeOnEsc={false}
          closeOnOverlayClick={false}
          autosaveId="polish-preview-drawer"
          contentProps={{
            bg: "white",
            minWidthPercent: 30,
            initialWidthPercent: 30,
          }}
          header={
            <Flex direction="column" gap={1}>
              <Text fontSize="lg" fontWeight="400">
                Draft with Avela Intelligence
              </Text>
              <Box>
                <Tag size="sm" variant="outline" colorScheme="purple" gap={1}>
                  <Icon as={RiFlaskLine} />
                  <Text>Beta</Text>
                </Tag>
              </Box>
            </Flex>
          }
          footer={
            <Flex direction="column" gap={2} width="100%">
              <Text fontSize="xs" color="gray.600" textAlign="center">
                Avela Intelligence can make mistakes. Review important
                information.
              </Text>
              <Flex direction="row" gap={3} justify="flex-end">
                <Button
                  flex={1}
                  size="sm"
                  variant="outline"
                  colorScheme="gray"
                  onClick={() => handleCancel(values, getFieldMeta)}
                >
                  Discard
                </Button>
                <Button
                  flex={1}
                  size="sm"
                  colorScheme="primary"
                  leftIcon={<RiCheckLine />}
                  onClick={() => handleConfirm(values, getFieldMeta)}
                >
                  Insert
                </Button>
              </Flex>
            </Flex>
          }
        >
          <VStack spacing={6} align="stretch" py={4}>
            {hasRecipientsWithEmail ? (
              <Flex direction="column" gap={2}>
                <FormMustacheInput
                  name="subject"
                  id="subject"
                  label="Message subject"
                  formLabelProps={{ fontSize: "sm", color: "gray.700" }}
                />
                <FormikLexicalEditor
                  name="email_lexical"
                  placeholder="Email content"
                  height="300px"
                  width="100%"
                  label="Email content"
                  labelProps={{ fontSize: "sm", color: "gray.700" }}
                  isRequired={true}
                  settings={emailEditorSettings}
                />
              </Flex>
            ) : (
              <Text color="blackAlpha.700" fontWeight="400">
                No email recipients
              </Text>
            )}

            {hasRecipientsWithPhone ? (
              <FormikLexicalEditor
                name="sms_lexical"
                placeholder="SMS content"
                height="150px"
                width="100%"
                editorVersion="lite"
                label="SMS content"
                labelProps={{ fontSize: "sm", color: "gray.700" }}
                isRequired={true}
                settings={smsEditorSettings}
              />
            ) : (
              <Text color="blackAlpha.700" fontWeight="400">
                No SMS recipients
              </Text>
            )}
          </VStack>
        </ResizableDrawer>
      )}
    </Formik>
  );
}

function signalPolishEdited(
  getFieldMeta: (name: string) => FieldMetaProps<MessageContent>,
  messageContext?: MessageContextSchema
) {
  const isEmailLexicalEdited = getFieldMeta("email_lexical").touched;
  const isSmsLexicalEdited = getFieldMeta("sms_lexical").touched;

  if (isEmailLexicalEdited || isSmsLexicalEdited) {
    amplitude.track("Edited ad-hoc polish", {
      ...messageContext,
      email_content_edited: isEmailLexicalEdited,
      sms_content_edited: isSmsLexicalEdited,
    });
  }
}
